import { Form, Input, InputNumber, Switch, Row, Col, Button, Tabs, Select } from "antd";
import type { TabsProps } from "antd/lib";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

import { Tenant } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";

import { onFinishFailed } from "../helpers";
import {
  IOX_DATETIME_BROWSER,
  IOX_DATETIME_DEFAULT,
  IOX_DATETIME_FORMAT_TAG_KEY,
  IOX_DATETIME_ISO_LOCAL,
  IOX_DATETIME_PRESET_OPTIONS,
  IOX_DATETIME_US_12H,
  normalizeIoxDatetimePreset,
} from "../../branding/datetimeBranding";

// ──────────────────────────────────────────────────────────────────────────
// TenantForm — split-permission tenant edit page.
//
// Post-Slice-5, tenant admins are allowed to edit their own tenant's
// display name, description, and datetime preference (previously the
// whole form was locked for non-server-admins, which made the name field
// read-only even though the subscription-name-vs-display-name mismatch
// is a common real complaint).
//
// Server admins retain full control: gateway caps, per-tenant switches,
// and the Tags tab (which carries the `iox:*` plan entitlements SM
// writes — letting a tenant admin edit these would let them grant
// themselves Pro features).
//
// The former "Tenant Configuration" tab is gone; its datetime control
// moved to the General tab, and its login-layout control was deleted
// entirely (the login page now uses a single fixed layout).
// ──────────────────────────────────────────────────────────────────────────

interface IProps {
  initialValues: Tenant;
  onFinish: (obj: Tenant) => void;
  /** True when the signed-in user is a ioX-Bridge server admin. */
  isGlobalAdmin: boolean;
}

function TenantForm(props: IProps) {
  const initialValues = props.initialValues.toObject();
  const initialDatetimeFormat = normalizeIoxDatetimePreset(
    props.initialValues.getTagsMap().get(IOX_DATETIME_FORMAT_TAG_KEY),
  );

  // Fields only server admins can touch. When a tenant admin edits, the
  // submit round-trips through the full form, so we must fall back to the
  // initial values for admin-only fields to keep them stable (otherwise
  // their `disabled` inputs would still submit `undefined`).
  const adminOnly = !props.isGlobalAdmin;

  const onFinish = (values: Tenant.AsObject) => {
    const datetimeFormat = normalizeIoxDatetimePreset(
      (values as Tenant.AsObject & { datetimeFormat?: string }).datetimeFormat,
    );
    const v = Object.assign(props.initialValues.toObject(), values);

    const tenant = new Tenant();
    tenant.setId(v.id);
    tenant.setName(v.name);
    tenant.setDescription(v.description);
    tenant.setCanHaveGateways(v.canHaveGateways);
    tenant.setMaxGatewayCount(v.maxGatewayCount);
    tenant.setMaxDeviceCount(v.maxDeviceCount);
    tenant.setPrivateGatewaysUp(v.privateGatewaysUp);
    tenant.setPrivateGatewaysDown(v.privateGatewaysDown);

    // Preserve ALL existing tags verbatim when a tenant admin submits —
    // they never see the Tags tab so `v.tagsMap` is whatever came in
    // (unchanged from initial). When a server admin submits, they may
    // have edited tags in the Tags tab and those edits flow through.
    // Either way we set them back wholesale, then overwrite the
    // datetime tag with whatever the General-tab Select produced.
    for (const elm of v.tagsMap) {
      tenant.getTagsMap().set(elm[0], elm[1]);
    }
    tenant.getTagsMap().set(IOX_DATETIME_FORMAT_TAG_KEY, datetimeFormat);

    props.onFinish(tenant);
  };

  const generalTab = (
    <>
      <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a name!" }]}>
        {/* Tenant admins CAN rename their own tenant — subscription-name
            ≠ what the customer wants displayed. Rename is local to
            ioX-Bridge; it is not written back to the Subscription
            Manager (SM only owns the initial-create name). */}
        <Input />
      </Form.Item>
      <Form.Item label="Description" name="description">
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        label="Date and time display"
        name="datetimeFormat"
        tooltip="How timestamps are shown across this tenant's console (devices, gateways, logs, etc.)."
        rules={[{ required: true, message: "Please select a date and time format!" }]}
      >
        <Select
          options={[
            { value: IOX_DATETIME_ISO_LOCAL, label: "24-hour (yyyy-MM-dd HH:mm:ss)" },
            { value: IOX_DATETIME_US_12H, label: "US 12-hour (Mar 27, 2026 3:09 PM)" },
            { value: IOX_DATETIME_BROWSER, label: "Match browser locale" },
          ]}
        />
      </Form.Item>
      <Row>
        <Col span={8}>
          <Form.Item
            label="Tenant can have gateways"
            name="canHaveGateways"
            tooltip="When checked, the tenant can add gateways. Note that the usage of the gateways is not limited to this tenant only unless these are marked private."
            valuePropName="checked"
          >
            <Switch disabled={adminOnly} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Gateways are private (uplink)"
            name="privateGatewaysUp"
            tooltip="Uplink received by gateways of this tenant can only be used by the devices of this tenant."
            valuePropName="checked"
          >
            <Switch disabled={adminOnly} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Gateways are private (downlink)"
            name="privateGatewaysDown"
            tooltip="Other tenants can not use the gateways of this tenant for downlinks. This can be useful in case uplinks are shared with other tenants, but you want to avoid other tenants using downlink airtime of your gateways."
            valuePropName="checked"
          >
            <Switch disabled={adminOnly} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Form.Item
            label="Max. gateway count"
            name="maxGatewayCount"
            tooltip="The maximum number of gateways that can be added by this tenant (0 = unlimited)."
          >
            <InputNumber min={0} disabled={adminOnly} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Max. device count"
            name="maxDeviceCount"
            required={true}
            tooltip="The maximum number of devices that can be added by this tenant (0 = unlimited)."
          >
            <InputNumber min={0} disabled={adminOnly} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const tagsTab = (
    <Form.List name="tagsMap">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Row gutter={24} key={key}>
              <Col span={6}>
                <Form.Item
                  {...restField}
                  name={[name, 0]}
                  rules={[{ required: true, message: "Please enter a key!" }]}
                >
                  <Input placeholder="Key" />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  {...restField}
                  name={[name, 1]}
                  rules={[{ required: true, message: "Please enter a value!" }]}
                >
                  <Input placeholder="Value" />
                </Form.Item>
              </Col>
              <Col span={2}>
                <MinusCircleOutlined onClick={() => remove(name)} />
              </Col>
            </Row>
          ))}
          <Form.Item>
            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
              Add tag
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );

  // The Tags tab is server-admin-only: it carries the `iox:*` plan
  // entitlements that SM writes. A tenant admin editing these could
  // silently grant themselves Pro features, so we don't even render it.
  const tabItems: TabsProps["items"] = [
    { key: "1", label: "General", children: generalTab },
    ...(props.isGlobalAdmin ? [{ key: "2", label: "Tags", children: tagsTab }] : []),
  ];

  return (
    <Form
      layout="vertical"
      initialValues={{
        ...initialValues,
        datetimeFormat: IOX_DATETIME_PRESET_OPTIONS.includes(initialDatetimeFormat)
          ? initialDatetimeFormat
          : IOX_DATETIME_DEFAULT,
      }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Tabs items={tabItems} />
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default TenantForm;
